import { createSlice } from "@reduxjs/toolkit";
import { LensTypes, RecordingTypes, Status } from "lib/enums";
import { RootState } from "reducers/store";
import { postCameraThunk as postCamera, deleteCameraThunk as deleteCamera, deleteCameraStreamThunk as deleteStream, putCameraThunk as putCamera, postDuplicateCameraThunk as postDuplicateCamera } from "./action";
import { Camera, CameraGroup, CameraRecording, CameraRecordingComplexCell, CameraRecordingDataRow, CameraStream } from "lib/types";

export type CamerasState = {
    data: Camera[]
    camerasStatus: Status
    backendErrors: {
        cameraStatusErrorMsg: string
    }
    upsertStatus: Status
}

const initialState: CamerasState = {
    data: [],
    camerasStatus: Status.Idle,
    backendErrors: {
        cameraStatusErrorMsg: ''
    },
    upsertStatus: Status.Idle
}

const cameraSlice = createSlice({
    name: 'cameras',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(postCamera.pending, state => {
                state.upsertStatus = Status.Pending
            })
            .addCase(postCamera.fulfilled, (state, action) => {
                state.upsertStatus = Status.Success
                state.backendErrors.cameraStatusErrorMsg = ''
            })
            .addCase(postCamera.rejected, (state, action) => {
                state.upsertStatus = Status.Rejected
                state.backendErrors.cameraStatusErrorMsg = action.payload?.error || 'Failed to add camera'
            })
            .addCase(putCamera.pending, (state) => {
                state.upsertStatus = Status.Pending;
            })
            .addCase(putCamera.fulfilled, (state, action) => {
                state.upsertStatus = Status.Success;
                state.backendErrors.cameraStatusErrorMsg = "";
            })
            .addCase(putCamera.rejected, (state, action) => {
                state.upsertStatus = Status.Rejected;
                state.backendErrors.cameraStatusErrorMsg = action.payload?.error || "Failed to edit camera";
            })
            .addCase(deleteCamera.pending, state => {
                state.upsertStatus = Status.Pending
            })
            .addCase(deleteCamera.fulfilled, (state, action) => {
                state.upsertStatus = Status.Success
                state.backendErrors.cameraStatusErrorMsg = ''
            })
            .addCase(deleteCamera.rejected, (state, action) => {
                state.upsertStatus = Status.Rejected
                state.backendErrors.cameraStatusErrorMsg = action.payload?.error || 'Failed to delete camera'
            })
            .addCase(postDuplicateCamera.pending, (state) => {
                state.upsertStatus = Status.Pending;
            })
            .addCase(postDuplicateCamera.fulfilled, (state, action) => {
                state.upsertStatus = Status.Success;
                state.backendErrors.cameraStatusErrorMsg = "";
            })
            .addCase(postDuplicateCamera.rejected, (state, action) => {
                state.upsertStatus = Status.Rejected;
                state.backendErrors.cameraStatusErrorMsg = action.payload?.error || "Failed to duplicate camera";
            })
            .addCase(deleteStream.pending, (state) => {
                state.upsertStatus = Status.Pending;
            })
            .addCase(deleteStream.fulfilled, (state) => {
                state.upsertStatus = Status.Success;
                state.backendErrors.cameraStatusErrorMsg = "";
            })
            .addCase(deleteStream.rejected, (state, action) => {
                state.upsertStatus = Status.Rejected;
                state.backendErrors.cameraStatusErrorMsg =
                    action.payload?.error || "Failed to delete stream";
            });
    ;},
});

export const selectisSiteDesignEditorUpdating = (state: RootState): boolean => {
    const flattenedStatuses = Object.values(state.cameras.camerasStatus)
        .flatMap(camerasStatus => {
            return Object.values(camerasStatus)
        });

    const hasPendingStatus = flattenedStatuses.some(status => status === Status.Pending)
    return hasPendingStatus || state.cameras.upsertStatus === Status.Pending
}

const getRecordingType = (recording: CameraRecording): RecordingTypes => {
    if (recording.continuousEnabled && recording.motionEnabled) {
        return RecordingTypes.Speedup
    } else if (recording.continuousEnabled) {
        return RecordingTypes.Constant
    } else {
        return RecordingTypes.Event
    }
}

const getMotion = (recording: CameraRecording): CameraRecordingComplexCell<number> => {
    if (recording.continuousEnabled && recording.motionEnabled) {
        return { continuous: 100 - recording.motionPercentage, motion: recording.motionPercentage }
    } else {
        return { continuous: 0, motion: recording.motionPercentage }
    }
}

const recordingDtoToDataRow = (recording: CameraRecording, retentionDays: number, quantity: number, groupId: number, configId: number, lensType: LensTypes): CameraRecordingDataRow => {
    return {
        id: recording.id,
        cameraGroupId: groupId,
        streamId: recording.streamId,
        quantity: quantity,
        lensType: lensType,
        recording: getRecordingType(recording),
        hoursPerDay: recording.hoursPerDay,
        retentionDays: retentionDays,
        codec: recording.codec?.domainValueId.toString(),
        fps: { continuous: recording.continuousFps, motion: recording.motionFps },
        motion: getMotion(recording),
        complexity: { continuous: recording.continuousComplexity?.domainValueId.toString(), motion: recording.motionComplexity?.domainValueId.toString() },
        resolution: { continuous: recording.continuousResolution?.domainValueId.toString(), motion: recording.motionResolution?.domainValueId.toString() },
        bitrateKbit: { continuous: Math.trunc(recording.continuousUserBitrate?.to_kbps ?? 0), motion: Math.trunc(recording.motionUserBitrate?.to_kbps ?? 0) },
        continuousIsCustomBitrate: recording.continuousUseUserBitrate,
        motionIsCustomBitrate: recording.motionUseUserBitrate
    }
}

export const defaultRecording: CameraRecording = {
    codec: { domainValueId: 3 },
    hoursPerDay: 24,
    continuousFps: 0,
    continuousComplexity: { domainValueId: 2 },
    continuousResolution: { domainValueId: 8 },
    startTime: {},
    motionFps: 15,
    motionComplexity: { domainValueId: 2 },
    motionResolution: { domainValueId: 8 },
    continuousEnabled: false,
    motionEnabled: true,
    continuousUseUserBitrate: false,
    motionUseUserBitrate: true,
    motionPercentage: 40,
    continuousUserBitrate: { bitPerSec: 2702000, to_kbps: 2702 },
    motionUserBitrate: { bitPerSec: 2702000, to_kbps: 2702 }
}

export const defaultStream: CameraStream = {
    name: '',
    retentionDays: 30
}

export const defaultGroup: CameraGroup = {
    name: '',
    quantity: 1
}

export const getDefaultRecordingDataRow = (): CameraRecordingDataRow => recordingDtoToDataRow(defaultRecording, defaultStream.retentionDays, defaultGroup.quantity, 0, 0, LensTypes.SingleLens)

export const selectAreCamerasLoading = (state: RootState): boolean => state.cameras.camerasStatus === Status.Pending

export default cameraSlice.reducer
